"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoteRepository = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const entity_service_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/entity-service.utils");
const constants_1 = require("../../../../service-lib/src/lib/constants");
const entities_1 = require("../../../../service-lib/src/lib/entities");
let NoteRepository = class NoteRepository {
    constructor(noteRepository, entityService) {
        this.noteRepository = noteRepository;
        this.entityService = entityService;
    }
    // Create a new note
    async createNote(noteDetails, entityManager) {
        try {
            // Note linking validation
            await this.validateNoteAssociations(entityManager, noteDetails.companyId, noteDetails.opportunityId, noteDetails.activityId);
            const note = entityManager.create(entities_1.Note, noteDetails);
            return await entityManager.save(entities_1.Note, note);
        }
        catch (error) {
            console.error(error);
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error creating note: " + error.message);
        }
    }
    // Create note documents
    async createNoteDocuments(documents, noteId, entityManager) {
        try {
            await Promise.all(documents.map(async (doc) => {
                if (doc.documentId) {
                    const document = await entityManager.findOne(entities_1.FileUpload, {
                        where: { id: doc.documentId },
                    });
                    if (!document) {
                        throw new common_1.NotFoundException(messages_1.errorMessages.documentWithIdNotFound(doc.documentId));
                    }
                }
            }));
            const noteDocuments = documents.map((doc) => ({
                noteId: noteId,
                documentId: doc.documentId,
            }));
            await entityManager.save(entities_1.NoteDocumentMap, noteDocuments);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error creating note document: " + error.message);
        }
    }
    // Get notes with pagination, search, and sorting
    async getNotes(page, limit, searchArray, sort, userId, searchBy) {
        try {
            const { data, count } = await this.entityService.fetchEntityList(entities_1.Note, page, limit, sort.length > 0 ? sort : [{ field: "id", order: "ASC" }], ["company", "opportunity", "opportunity.policyType", "activity"], { createdBy: userId }, undefined, searchArray.length === 0 ? [] : searchArray, undefined, searchBy, constants_1.noteSearchObject);
            if (!data || data.length === 0) {
                return { data: [], count: 0 };
            }
            return { data: await this.transformGetNotesResponse(data), count };
        }
        catch (error) {
            throw new Error("Failed to fetch get notes list", error);
        }
    }
    // Get a note by ID
    async getNoteById(id) {
        try {
            const note = await this.noteRepository.findOne({
                where: { id },
                relations: [
                    "company",
                    "opportunity",
                    "opportunity.policyType",
                    "activity",
                    "noteDocs",
                ],
            });
            if (!note) {
                throw new common_1.NotFoundException(messages_1.errorMessages.noteNotFound);
            }
            const data = await this.transformNoteResponse([{ ...note }]);
            return data[0];
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error fetching note: " + error.message);
        }
    }
    // Get notes by opportunity ID
    async updateNote(id, updateNote, entityManager) {
        try {
            const note = await entityManager.findOne(entities_1.Note, { where: { id } });
            if (!note) {
                throw new common_1.NotFoundException(messages_1.errorMessages.noteNotFound);
            }
            // Validate associations before updating
            await this.validateNoteAssociations(entityManager, updateNote.companyId, updateNote.opportunityId, updateNote.activityId);
            await entityManager.update(entities_1.Note, id, updateNote);
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            if (error instanceof common_1.BadRequestException)
                throw error;
            throw new Error("Error updating note: " + error.message);
        }
    }
    // Update note documents
    async updateNoteDocuments(documents, noteId, entityManager) {
        try {
            const existingDocuments = await entityManager.find(entities_1.NoteDocumentMap, {
                where: { noteId },
            });
            const existingDocumentIds = existingDocuments.map((doc) => Number(doc.documentId));
            // Add new documents
            const newDocuments = documents.filter((doc) => doc.documentId && !existingDocumentIds.includes(doc.documentId));
            if (newDocuments.length > 0) {
                await this.createNoteDocuments(newDocuments, noteId, entityManager);
            }
            // Remove documents that are not in the documents array
            const documentsToRemove = existingDocuments.filter((doc) => !documents.some((d) => d.documentId === doc.documentId));
            if (documentsToRemove.length > 0) {
                await entityManager.remove(entities_1.NoteDocumentMap, documentsToRemove);
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error updating note document: " + error.message);
        }
    }
    // Delete a note by ID
    async deleteNoteById(id) {
        try {
            const note = await this.noteRepository.findOne({ where: { id } });
            if (!note) {
                throw new common_1.NotFoundException(messages_1.errorMessages.noteNotFound);
            }
            await this.noteRepository.softDelete({ id });
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException)
                throw error;
            throw new Error("Error deleting note: " + error.message);
        }
    }
    // Transform note response to match the required format
    async transformNoteResponse(notes) {
        try {
            return notes.map((note) => ({
                id: note.id,
                title: note.title,
                description: note.description,
                noteDocs: note?.noteDocs?.map((doc) => ({
                    documentId: doc.documentId,
                })),
                companyId: undefined,
                company: note?.company
                    ? {
                        id: note.company.id,
                        name: note.company.companyName,
                        displayName: note.company.displayName,
                    }
                    : undefined,
                opportunityId: undefined,
                opportunity: note?.opportunity
                    ? {
                        opportunityId: note.opportunity.opportunityId,
                        policy: note.opportunity.policyType?.lookUpValue,
                    }
                    : undefined,
                activityId: undefined,
                activity: note?.activity
                    ? {
                        id: note.activity.id,
                        activityName: note.activity.activityName,
                    }
                    : undefined,
            }));
        }
        catch (error) {
            throw new Error("Failed to transform note response", error);
        }
    }
    // Transform get notes response to match the required format
    async transformGetNotesResponse(notes) {
        try {
            return notes.map((note) => ({
                id: note.id,
                title: note.title,
                description: note.description,
                companyId: undefined,
                company: note?.company
                    ? {
                        id: note.company.id,
                        name: note.company.companyName,
                        displayName: note.company.displayName,
                    }
                    : undefined,
                opportunityId: undefined,
                opportunity: note?.opportunity
                    ? {
                        opportunityId: note.opportunity.opportunityId,
                        policy: note.opportunity.policyType?.lookUpValue,
                    }
                    : undefined,
                activityId: undefined,
                activity: note?.activity
                    ? {
                        id: note.activity.id,
                        activityName: note.activity.activityName,
                    }
                    : undefined,
            }));
        }
        catch (error) {
            throw new Error("Failed to transform get notes response", error);
        }
    }
    // Validate associations for note creation or update
    async validateNoteAssociations(entityManager, companyId, opportunityId, activityId) {
        try {
            if (companyId) {
                const company = await entityManager.findOne(entities_1.Company, {
                    where: { id: companyId },
                });
                if (!company) {
                    throw new common_1.NotFoundException(messages_1.errorMessages.companyNotFound);
                }
            }
            if (opportunityId) {
                const opportunity = await entityManager.findOne(entities_1.Opportunity, {
                    where: { opportunityId },
                });
                if (!opportunity) {
                    throw new common_1.NotFoundException(messages_1.errorMessages.opportunityNotFound);
                }
            }
            if (opportunityId && activityId) {
                const activities = await entityManager.find(entities_1.OpportunityActivityMap, {
                    where: { opportunityId: opportunityId },
                });
                if (!activities || activities.length === 0) {
                    throw new common_1.BadRequestException("No activities found for the given opportunity ID");
                }
                const activityExists = activities.some((activity) => activity.id === activityId);
                if (!activityExists) {
                    throw new common_1.BadRequestException(`Activity ID ${activityId} not found for the given opportunity ID`);
                }
            }
            else if (!opportunityId && activityId) {
                throw new common_1.BadRequestException("Activity ID provided without an associated Opportunity ID");
            }
        }
        catch (error) {
            if (error instanceof common_1.NotFoundException) {
                throw error;
            }
            else if (error instanceof common_1.BadRequestException) {
                throw error;
            }
        }
    }
};
exports.NoteRepository = NoteRepository;
exports.NoteRepository = NoteRepository = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(entities_1.Note)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof entity_service_utils_1.EntityService !== "undefined" && entity_service_utils_1.EntityService) === "function" ? _b : Object])
], NoteRepository);
